import { useRef, useState, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Carregando, Erro, Vazio } from '../componentes/Estados.js';
import { PreviewImportacao } from '../componentes/PreviewImportacao.js';
import { moedaBr } from '../estado/gerador.js';
import { rotuloTabela } from '../lib/formato.js';
import { ApiError, mensagemDoErro, obterAccessToken, requisitar } from '../lib/api.js';
import { slugAtual } from '../lib/tenant.js';
import type { Preview } from '../tipos/importacao.js';
import './Importacao.css';

/**
 * Importação de tabela de preço — Story 8.4 (RF-11/12/13), **admin-only**.
 *
 * O fluxo tem três passos deliberadamente separados: subir → **conferir** →
 * efetivar. O passo do meio é a razão de a tela existir; sem ele, trocar a
 * tabela do trimestre é um salto no escuro, e trocar preço errado é o tipo de
 * engano que só aparece no orçamento do cliente.
 */

interface Versao {
  id: string;
  status: string;
  vigenciaInicio: string;
  criadoEm: string;
  criadaPor: string | null;
  /**
   * A versao EM VIGOR — vem do servidor, nao e deduzida aqui.
   *
   * O historico tem varias "Efetivada" e so uma vale. Deduzir na tela (a mais
   * recente, por exemplo) criaria uma segunda opiniao sobre qual tabela manda,
   * e o dia em que ela discordasse do servidor seria o dia em que alguem
   * precisava saber.
   */
  vigente: boolean;
  efetivadaEm: string | null;
  efetivadaPor: string | null;
}

interface ResultadoUpload {
  versionId: string;
  produtos: number;
  precos: number;
  ignorados: number;
}

/**
 * As quatro **tabelas de preço** que a fábrica manda a cada trimestre.
 *
 * A tela pedia dois orçamentos à vista até a correção do parser. Orçamento é a
 * saída do processo, não a entrada: exigi-lo obrigava alguém a rodar o script
 * antigo antes de poder importar, e era isso que travava o ciclo trimestral.
 */
const CAMPOS = [
  {
    nome: 'tabelaRt10' as const,
    rotulo: 'Tabela RT 10%',
    // A RT 5 (só rodapé) entra pela importação dedicada, não por este arquivo.
    ajuda: `Preço-base da tabela RT 10 (no orçamento, ${rotuloTabela('RT10')}) — aba "COM RT DE 10%", coluna comissão 5% sem del credere.`,
  },
  {
    nome: 'tabelaDifal' as const,
    rotulo: 'Tabela PE Difal 16,98%',
    ajuda: `Faz dois papéis: a aba "ENGENHARIA" é o preço-base da tabela ${rotuloTabela('CPF')}, e a "COM DEL CREDERE" é o preço de todo produto Villa Arte.`,
  },
  {
    nome: 'tabelaVinilico' as const,
    rotulo: 'Tabela de vinílico e rodapé',
    ajuda:
      'Traz os rodapés (coluna PREÇO 7%, por metro linear), que não existem em nenhuma outra tabela.',
  },
  {
    nome: 'tabelaVillaArte' as const,
    rotulo: 'Tabela Villa Art',
    ajuda:
      'Confere a lista de Villa Arte. O preço praticado é o do del credere, na tabela Difal — a coluna 7% desta aqui é outra condição comercial.',
  },
];

type NomeCampo = (typeof CAMPOS)[number]['nome'];

function dataBr(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function Importacao(): JSX.Element {
  const [arquivos, setArquivos] = useState<Partial<Record<NomeCampo, File>>>({});
  const [rotulo, setRotulo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<unknown>(undefined);

  const [versionId, setVersionId] = useState<string | undefined>(undefined);
  const [resumoUpload, setResumoUpload] = useState<ResultadoUpload | undefined>(undefined);
  const [preview, setPreview] = useState<Preview | undefined>(undefined);
  const [carregandoPreview, setCarregandoPreview] = useState(false);

  const [efetivando, setEfetivando] = useState(false);
  /** Id em descarte — trava so a linha clicada, nao a tabela inteira. */
  const [descartando, setDescartando] = useState<string | undefined>(undefined);
  const [efetivada, setEfetivada] = useState<string | undefined>(undefined);

  const historico = useQuery({
    queryKey: ['importacoes'],
    queryFn: async () => (await requisitar<Versao[]>('/api/importacoes')).dados,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const faltam = CAMPOS.filter((c) => arquivos[c.nome] === undefined);
  const podeEnviar = faltam.length === 0 && !enviando;

  async function enviar(): Promise<void> {
    setErro(undefined);
    setEnviando(true);
    try {
      const forma = new FormData();
      for (const c of CAMPOS) {
        const f = arquivos[c.nome];
        if (f !== undefined) forma.append(c.nome, f, f.name);
      }
      if (rotulo.trim() !== '') forma.append('rotulo', rotulo.trim());

      // `FormData` não passa pelo cliente de API: ele serializa JSON e define
      // `content-type`, e num multipart o boundary tem de ser do navegador.
      const cabecalhos = new Headers();
      const token = obterAccessToken();
      if (token !== undefined) cabecalhos.set('authorization', `Bearer ${token}`);
      const slug = slugAtual();
      if (slug !== undefined) cabecalhos.set('x-tenant-slug', slug);

      const resposta = await fetch('/api/importacoes', {
        method: 'POST',
        headers: cabecalhos,
        body: forma,
        credentials: 'same-origin',
      });

      const corpo = (await resposta.json()) as {
        success: boolean;
        data?: ResultadoUpload;
        error?: { code?: string; message?: string };
      };
      if (!resposta.ok || !corpo.success) {
        throw new ApiError(
          corpo.error?.code ?? 'INTERNAL_ERROR',
          corpo.error?.message ?? 'Não foi possível processar as planilhas.',
          resposta.status,
        );
      }

      const dados = corpo.data as ResultadoUpload;
      setResumoUpload(dados);
      setVersionId(dados.versionId);
      setEfetivada(undefined);
      await carregarPreview(dados.versionId);
      await historico.refetch();
    } catch (e) {
      setErro(e);
    } finally {
      setEnviando(false);
    }
  }

  /** Abre no painel de cima o preview de um rascunho do historico. */
  async function verPreview(id: string): Promise<void> {
    setErro(undefined);
    setVersionId(id);
    setEfetivada(undefined);
    await carregarPreview(id);
  }

  async function descartar(id: string): Promise<void> {
    setErro(undefined);
    setDescartando(id);
    try {
      await requisitar(`/api/importacoes/${id}`, { metodo: 'DELETE' });
      // Se era o rascunho aberto no painel, fecha junto — deixa-lo na tela
      // ofereceria "Efetivar" para algo que nao existe mais.
      if (versionId === id) {
        setVersionId(undefined);
        setPreview(undefined);
        setResumoUpload(undefined);
      }
      await historico.refetch();
    } catch (e) {
      setErro(e);
    } finally {
      setDescartando(undefined);
    }
  }

  async function carregarPreview(id: string): Promise<void> {
    setCarregandoPreview(true);
    try {
      setPreview(await requisitar<Preview>(`/api/importacoes/${id}/preview`).then((r) => r.dados));
    } catch (e) {
      setErro(e);
    } finally {
      setCarregandoPreview(false);
    }
  }

  /**
   * Efetiva uma versao — o id vem de FORA, nao do estado da sessao.
   *
   * Ate a UX.2 esta funcao lia `versionId` do `useState`, que so era preenchido
   * no submit do upload. Recarregar a pagina zerava o estado e o rascunho ficava
   * inalcancavel: existia no banco e nao havia caminho na tela ate ele. O unico
   * jeito era refazer o upload — que criava OUTRO rascunho.
   */
  async function efetivar(id: string): Promise<void> {
    setErro(undefined);
    setEfetivando(true);
    try {
      await requisitar(`/api/importacoes/${id}/efetivar`, { metodo: 'POST', corpo: {} });
      setEfetivada(id);
      setVersionId(id);
      await carregarPreview(id);
      await historico.refetch();
    } catch (e) {
      setErro(e);
    } finally {
      setEfetivando(false);
    }
  }

  return (
    <section className="pagina">
      <header className="pagina__cabecalho">
        <div>
          <h1>Tabelas de preço</h1>
          <p className="pagina__sub">
            Suba as planilhas do trimestre, confira o que muda e só então efetive.
          </p>
        </div>
      </header>

      {erro !== undefined && (
        <p className="aviso" role="alert">
          {mensagemDoErro(erro)}
        </p>
      )}

      {/* ---------- 1. Upload ---------- */}
      <div className="cartao importacao__passo">
        <h2 className="importacao__titulo">
          <span className="importacao__numero">1</span> Enviar as planilhas
        </h2>

        <div className="importacao__campos">
          {CAMPOS.map((c) => (
            <CampoArquivo
              key={c.nome}
              rotulo={c.rotulo}
              ajuda={c.ajuda}
              arquivo={arquivos[c.nome]}
              desabilitado={enviando}
              aoEscolher={(f) => {
                setArquivos((a) => ({ ...a, [c.nome]: f }));
              }}
            />
          ))}
        </div>

        <label className="campo importacao__rotulo">
          <span className="campo__rotulo">Identificação (opcional)</span>
          <input
            className="campo__entrada"
            value={rotulo}
            placeholder="ex.: tabela 3T26"
            disabled={enviando}
            onChange={(e) => {
              setRotulo(e.target.value);
            }}
          />
        </label>

        <div className="importacao__acoes">
          {faltam.length > 0 && (
            <span className="importacao__pendente">
              Faltam {faltam.length} de {CAMPOS.length} arquivos.
            </span>
          )}
          <button
            type="button"
            className="botao botao--primario"
            disabled={!podeEnviar}
            onClick={() => {
              void enviar();
            }}
          >
            {enviando ? 'Processando…' : 'Enviar e pré-visualizar'}
          </button>
        </div>
      </div>

      {/* ---------- 2. Preview ---------- */}
      {versionId !== undefined && (
        <div className="cartao importacao__passo">
          <h2 className="importacao__titulo">
            <span className="importacao__numero">2</span> Conferir o que muda
          </h2>

          {resumoUpload !== undefined && (
            <p className="importacao__resumo-upload">
              Rascunho criado com <strong>{resumoUpload.produtos}</strong> produtos e{' '}
              <strong>{resumoUpload.precos}</strong> preços
              {resumoUpload.ignorados > 0 ? ` · ${String(resumoUpload.ignorados)} ignorados` : ''}.
              Nada disso vale ainda — a tabela em uso continua sendo a anterior.
            </p>
          )}

          {carregandoPreview ? (
            <Carregando rotulo="Comparando com a tabela em vigor…" />
          ) : preview === undefined ? (
            <Erro
              erro={erro}
              aoRepetir={() => {
                void carregarPreview(versionId);
              }}
            />
          ) : (
            <PreviewImportacao preview={preview} />
          )}
        </div>
      )}

      {/* ---------- 3. Efetivar ---------- */}
      {preview !== undefined && (
        <div className="cartao importacao__passo">
          <h2 className="importacao__titulo">
            <span className="importacao__numero">3</span> Efetivar
          </h2>

          {efetivada === versionId && efetivada !== undefined ? (
            <p className="nota nota--ok" role="status">
              Tabela efetivada. Ela passa a valer para os próximos orçamentos; os já emitidos
              continuam com os preços da data em que foram fechados.
            </p>
          ) : (
            <>
              <p className="nota">
                A versão em vigor <strong>não é apagada</strong> — ela fica no histórico, e todo
                orçamento já emitido continua apontando para a versão dele. A vigência é{' '}
                <strong>agora</strong>: agendar para uma data futura ainda não é suportado.
              </p>
              <div className="importacao__acoes">
                <button
                  type="button"
                  className="botao botao--primario"
                  disabled={efetivando || preview.status !== 'draft'}
                  onClick={() => {
                    if (versionId !== undefined) void efetivar(versionId);
                  }}
                >
                  {efetivando ? 'Efetivando…' : 'Efetivar esta tabela'}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ---------- Histórico ---------- */}
      <div className="cartao">
        <h2 className="importacao__titulo importacao__titulo--historico">Histórico de versões</h2>
        {historico.isPending ? (
          <Carregando rotulo="Carregando o histórico…" />
        ) : historico.isError ? (
          <Erro
            erro={historico.error}
            aoRepetir={() => {
              void historico.refetch();
            }}
          />
        ) : historico.data.length === 0 ? (
          <Vazio
            titulo="Nenhuma versão ainda"
            descricao="A primeira importação cria a tabela inicial."
          />
        ) : (
          <div className="tabela-rolagem">
            <table className="tabela tabela--cards">
              <thead>
                <tr>
                  <th scope="col">Criada em</th>
                  <th scope="col">Vigência</th>
                  <th scope="col">Efetivada em</th>
                  <th scope="col">Situação</th>
                  <th scope="col">Ações</th>
                </tr>
              </thead>
              <tbody>
                {historico.data.map((v) => (
                  <tr key={v.id} className={v.vigente ? 'importacao__linha--vigente' : undefined}>
                    <td data-rotulo="Criada em">{dataBr(v.criadoEm)}</td>
                    <td data-rotulo="Vigência">{dataBr(v.vigenciaInicio)}</td>
                    <td data-rotulo="Efetivada em">
                      {v.efetivadaEm === null ? '—' : dataBr(v.efetivadaEm)}
                    </td>
                    <td data-rotulo="Situação">{situacao(v)}</td>
                    <td className="importacao__acoes" data-rotulo="">
                      {v.status === 'draft' ? (
                        <>
                          <button
                            type="button"
                            className="botao botao--discreto"
                            onClick={() => {
                              void verPreview(v.id);
                            }}
                          >
                            Ver o que muda
                          </button>
                          <button
                            type="button"
                            className="botao botao--primario"
                            disabled={efetivando || descartando !== undefined}
                            onClick={() => {
                              if (
                                window.confirm(
                                  'Efetivar esta tabela? Ela passa a valer em todo orçamento novo.',
                                )
                              ) {
                                void efetivar(v.id);
                              }
                            }}
                          >
                            Efetivar
                          </button>
                          <button
                            type="button"
                            className="botao botao--discreto importacao__descartar"
                            disabled={descartando !== undefined || efetivando}
                            onClick={() => {
                              if (
                                window.confirm(
                                  'Descartar este rascunho? Os preços dele são apagados. A tabela em vigor não muda.',
                                )
                              ) {
                                void descartar(v.id);
                              }
                            }}
                          >
                            {descartando === v.id ? 'Descartando…' : 'Descartar'}
                          </button>
                        </>
                      ) : (
                        /*
                         * Efetivada nao tem acao destrutiva — nem a em vigor, nem
                         * uma historica. Ambas sao referenciadas por orcamentos
                         * emitidos (`quote.version_id`), e o servidor recusa com
                         * 409; nao oferecer o botao evita o caminho que termina em
                         * erro.
                         */
                        <span className="importacao__sem-acao">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

/** Campo de arquivo com o papel explicado — não "arraste 4 arquivos". */
function CampoArquivo({
  rotulo,
  ajuda,
  arquivo,
  desabilitado,
  aoEscolher,
}: {
  rotulo: string;
  ajuda: string;
  arquivo: File | undefined;
  desabilitado: boolean;
  aoEscolher: (f: File) => void;
}): JSX.Element {
  const entrada = useRef<HTMLInputElement>(null);

  return (
    <div className={arquivo === undefined ? 'arquivo' : 'arquivo arquivo--ok'}>
      <label className="arquivo__rotulo" htmlFor={`arq-${rotulo}`}>
        {rotulo}
      </label>
      <p className="arquivo__ajuda">{ajuda}</p>
      <input
        id={`arq-${rotulo}`}
        ref={entrada}
        className="arquivo__entrada"
        type="file"
        accept=".xlsx"
        disabled={desabilitado}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f !== undefined) aoEscolher(f);
        }}
      />
      {arquivo !== undefined && (
        <p className="arquivo__escolhido">
          {arquivo.name} · {moedaBr(String(arquivo.size / 1024), 0)} KB
        </p>
      )}
    </div>
  );
}

/**
 * A situacao da versao — TRES estados, nao dois.
 *
 * "Efetivada" e "em vigor" nao sao a mesma coisa: o historico acumula varias
 * efetivadas e apenas uma vale. Ate a UX.4 a tela mostrava todas com a mesma
 * etiqueta verde, e nao havia como responder "qual e a tabela boa?" olhando
 * para ela — a pergunta que a tela existe para responder.
 *
 * Quem decide e o servidor (`vigente`), pela MESMA funcao que a precificacao
 * usa para resolver o preco.
 */
function situacao(v: Versao): JSX.Element {
  if (v.vigente) {
    return (
      <strong className="etiqueta etiqueta--vigente" title="É esta que vale nos orçamentos de hoje">
        Em vigor
      </strong>
    );
  }
  if (v.status === 'efetivada') {
    return (
      <span className="etiqueta etiqueta--historica" title="Foi efetivada, mas outra a substituiu">
        Efetivada
      </span>
    );
  }
  return <span className="etiqueta">Rascunho</span>;
}
